import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Eye, FileText } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { StatusPill, LoadingScreen, EmptyState } from "@/components/common";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminVerification() {
  const [agencies, setAgencies] = useState(null);
  const [docs, setDocs] = useState([]);
  const load = () => {
    api.get("/admin/agencies").then((r) => setAgencies(r.data.filter((a) => ["PENDING", "UNDER_REVIEW"].includes(a.verification_status)))).catch(() => setAgencies([]));
    api.get("/admin/documents/pending").then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const verify = async (a, status) => {
    try { await api.post(`/admin/agencies/${a.id}/verify`, { status }); toast.success(`${a.name} → ${status}`); load(); } catch (e) { if (!e.isAuthError) toast.error("Could not update status. Please try again."); }
  };
  const reviewDoc = async (d, status) => {
    try { await api.post(`/admin/documents/${d.id}/review`, { status }); toast.success(`Document ${status}`); load(); } catch (e) { if (!e.isAuthError) toast.error("Could not update document. Please try again."); }
  };

  const docUrl = (d) => d.view_url ? `${BACKEND}${d.view_url}` : (d.file_id ? `${BACKEND}/api/files/doc/${d.file_id}` : null);

  return (
    <DashboardShell menu={adminMenu} title="Agency Verification">
      <PageHeader title="Verification Queue" subtitle="Review compliance and control Verified status. Uploading documents alone never grants verification." />
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 mb-6">
        Verification is controlled exclusively here. An agency is marked legally verified only after your review — never automatically.
      </div>

      {!agencies ? <LoadingScreen /> : agencies.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Queue is clear" hint="No agencies awaiting verification right now." />
      ) : (
        <div className="space-y-3 mb-10">
          {agencies.map((a) => (
            <div key={a.id} className="bg-white border border-navy-200 rounded-xl p-5 flex items-center justify-between flex-wrap gap-4" data-testid={`verify-agency-${a.id}`}>
              <div>
                <div className="flex items-center gap-2"><h3 className="font-heading font-semibold text-navy-900">{a.name}</h3><StatusPill status={a.verification_status} /></div>
                <p className="text-sm text-navy-500 mt-1">{a.head_office?.city} · PSARA: {a.psara_number || "—"} · GST: {a.gst_number || "—"} · {a.years_experience}y exp</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-navy-300" onClick={() => verify(a, "UNDER_REVIEW")} data-testid={`review-${a.id}`}>Mark Under Review</Button>
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => verify(a, "VERIFIED")} data-testid={`approve-${a.id}`}><Check size={15} className="mr-1" /> Verify</Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => verify(a, "REJECTED")} data-testid={`reject-${a.id}`}><X size={15} className="mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">Pending Documents ({docs.length})</h3>
      {docs.length === 0 ? <p className="text-navy-500 text-sm">No documents pending review.</p> : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-3">Agency</th><th className="text-left p-3">Document</th><th className="p-3">Expiry</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t border-navy-100" data-testid={`doc-${d.id}`}>
                  <td className="p-3 text-navy-800">{d.agency_name}</td>
                  <td className="p-3 flex items-center gap-2 text-navy-700"><FileText size={15} className="text-brand" /> {d.doc_type}</td>
                  <td className="p-3 text-center text-navy-600">{d.expiry_date || "—"}</td>
                  <td className="p-3 text-center"><StatusPill status={d.status} /></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {docUrl(d)
                      ? <a href={docUrl(d)} target="_blank" rel="noreferrer" className="inline-block mr-2"><Button size="sm" variant="outline" className="border-navy-300" data-testid={`doc-view-${d.id}`}><Eye size={14} className="mr-1" /> View</Button></a>
                      : <span className="text-xs text-navy-400 mr-2">No file</span>}
                    <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 mr-2" onClick={() => reviewDoc(d, "VERIFIED")} data-testid={`doc-approve-${d.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600" onClick={() => reviewDoc(d, "REJECTED")} data-testid={`doc-reject-${d.id}`}>Reject</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
